import { useEffect, useState } from 'react';
import { EditorDeRelatorio } from '../componentes/EditorDeRelatorio.tsx';
import { apiCasos, apiRelatorios, ErroDaApi, type Caso, type Relatorio } from '../lib/api.ts';
import { Icone } from '../componentes/Icone.tsx';
import { usarEhMaster } from '../lib/papel.ts';
import { avancoDoRelatorio } from '../lib/avanco-do-relatorio.ts';
import { PilulaDoFundo } from '../componentes/PilulaDoFundo.tsx';
import {
  contarPorFiltro,
  FILTROS_DA_LISTA,
  listaVisivel,
  ORDENS_DA_LISTA,
  type FiltroDaLista,
  type OrdemDaLista,
} from '../lib/lista-de-relatorios.ts';
import '../estilos/sistema.css';
import '../estilos/lista-de-relatorios.css';

/* ⭐ PRD §73: o filtro e a ordem são lembrados NESTE navegador, como o menu
   recolhido e a vista do Marketing. Sem acesso ao armazenamento (janela
   anônima), a lista abre como vem de fábrica e funciona igual. ⛔ A busca NÃO
   é lembrada: a aba abriria no dia seguinte com relatório "sumido". */
const CHAVE_DO_FILTRO = 'kairos-relatorios-filtro';
const CHAVE_DA_ORDEM = 'kairos-relatorios-ordem';

function escolhaLembrada<T extends string>(chave: string, validas: readonly T[], deFabrica: T): T {
  try {
    const v = localStorage.getItem(chave);
    return validas.find((x) => x === v) ?? deFabrica;
  } catch {
    return deFabrica;
  }
}

function lembrar(chave: string, valor: string) {
  try {
    localStorage.setItem(chave, valor);
  } catch {
    /* sem armazenamento, a escolha vale até fechar a aba */
  }
}

const FILTROS: Record<FiltroDaLista, { nome: string; icone: 'relatorios' | 'relogio' | 'concluido' }> = {
  todos: { nome: 'Todos', icone: 'relatorios' },
  andamento: { nome: 'Em andamento', icone: 'relogio' },
  entregues: { nome: 'Entregues', icone: 'concluido' },
};

/* ⚠️ "Crescente" e "decrescente" são as palavras dele no pedido, e cabem no
   celular ao lado do filtro; "Nº, maior primeiro" era cortado a 390 px. */
const ORDENS: Record<OrdemDaLista, string> = {
  'numero-desc': 'Nº decrescente',
  'numero-asc': 'Nº crescente',
  'devedor-az': 'Devedor, A a Z',
  'devedor-za': 'Devedor, Z a A',
};

/**
 * A aba Relatórios — o produto que a KAIRÓS vende.
 *
 * **Nasce aqui, vinculado ao caso** (decisão do Richard, 16/08/2026): a lista
 * mostra o que existe, e "Novo relatório" escolhe entre os casos que ainda não
 * têm um. Do caso vêm cliente, nível, prazo e investigados, que não se
 * redigitam.
 *
 * ⚠️ **Não há botão de "buscar nas plataformas", e a falta é proposital.**
 * Nenhuma fonte está contratada (`docs/PRD.md` §18.2) e boa parte da lista é
 * portal com captcha. Oferecer um botão que não busca nada seria pior que não
 * ter botão: a pessoa clica, não acontece, e passa a desconfiar do resto.
 */

export function Relatorios({
  aberto,
  aoAbrir,
  aoAbrirCliente,
  aoExpirarSessao,
}: {
  /** Qual relatório está aberto — vem do endereço, não de estado local. */
  aberto: string | null;
  aoAbrir: (id: string | null) => void;
  aoAbrirCliente: (id: string) => void;
  aoExpirarSessao: () => void;
}) {
  const [relatorios, setRelatorios] = useState<Relatorio[] | null>(null);
  const [casos, setCasos] = useState<Caso[]>([]);
  const [escolhendo, setEscolhendo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  /* De fábrica: Todos, e o número maior primeiro — o mais novo em cima (§73). */
  const [filtro, setFiltro] = useState<FiltroDaLista>(() =>
    escolhaLembrada(CHAVE_DO_FILTRO, FILTROS_DA_LISTA, 'todos'),
  );
  const [ordem, setOrdem] = useState<OrdemDaLista>(() =>
    escolhaLembrada(CHAVE_DA_ORDEM, ORDENS_DA_LISTA, 'numero-desc'),
  );
  /* ⚠️ Vive enquanto a aba está aberta: abrir um relatório e voltar mantém a
     busca, porque esta página continua montada por baixo do editor. */
  const [busca, setBusca] = useState('');
  const ehMaster = usarEhMaster();

  function filtrar(f: FiltroDaLista) {
    setFiltro(f);
    lembrar(CHAVE_DO_FILTRO, f);
  }

  function ordenar(o: OrdemDaLista) {
    setOrdem(o);
    lembrar(CHAVE_DA_ORDEM, o);
  }

  async function carregar() {
    try {
      const [r, c] = await Promise.all([apiRelatorios.listar(), apiCasos.listar()]);
      setRelatorios(r.relatorios);
      setCasos(c.casos);
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      setErro(e instanceof Error ? e.message : 'Não consegui carregar os relatórios.');
    }
  }

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function abrirDoCaso(casoId: string) {
    try {
      const r = await apiRelatorios.abrir(casoId);
      setEscolhendo(false);
      await carregar();
      aoAbrir(r.id);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui abrir o relatório.');
    }
  }

  if (aberto) {
    return (
      <EditorDeRelatorio
        id={aberto}
        aoAbrirCliente={aoAbrirCliente}
        aoExpirarSessao={aoExpirarSessao}
      />
    );
  }

  /* Caso que já tem relatório não aparece na escolha: abrir de novo devolveria
     o mesmo, e a lista sugeriria que dá para ter dois. */
  const comRelatorio = new Set((relatorios ?? []).map((r) => r.caso.id));
  const disponiveis = casos.filter((c) => !comRelatorio.has(c.id));

  /* ⭐ PRD §73: a busca, o filtro e a ordem, no navegador e só sobre o que a
     linha mostra (`lib/lista-de-relatorios.ts`). */
  const contagem = contarPorFiltro(relatorios ?? [], busca);
  const visiveis = listaVisivel(relatorios ?? [], { filtro, busca, ordem });

  return (
    /*
      ⭐ **Sistema visual 2026, parte 4 — a lista de Relatórios, 14/09/2026.** A
      folha de identidade, a escolha do caso e a lista em folhas. ⛔ **Mudou a
      aparência, não o que se clica:** o código e "Escrever" abrem o
      relatório, a linha do cliente abre a ficha, e "Novo relatório" escolhe
      entre os casos sem relatório.
    */
    <section className="pagina relatorios-2026">
      <section className="s-folha s-identidade relatorios-identidade">
        <div className="s-monograma relatorios-monograma" aria-hidden="true">
          <Icone nome="relatorios" tamanho={28} />
        </div>
        <div className="relatorios-identidade-texto">
          <span className="s-sobrelinha">Trabalho</span>
          <h1 className="titulo">Relatórios</h1>
          {/*
            ⚠️ **O subtítulo dizia "as seções vêm do escopo do nível — as mesmas
            que a proposta prometeu", e deixou de ser verdade em 20/08/2026.**

            A aba está sendo redefinida do zero: a estrutura foi zerada e será
            montada seção por seção com o Richard. Texto de tela que promete o
            que o sistema não faz é pior que tela sem texto — quem lê confia.
          */}
          <p className="relatorios-apoio">O documento que o cliente recebe.</p>
        </div>
        {/*
          ⚠️ **O botão não nasce mais desabilitado.** Ele ficava morto quando
          todos os casos já tinham relatório, e botão morto sem explicação lê-se
          como tela quebrada — foi o que o Richard encontrou em 16/08/2026.
          Agora ele abre e o painel diz por que não há o que escolher.
        */}
        <button
          type="button"
          className="botao-primario s-botao"
          onClick={() => setEscolhendo(true)}
        >
          <Icone nome="mais" tamanho={15} />
          Novo relatório
        </button>
      </section>

      {erro && <p className="erro">{erro}</p>}

      {escolhendo && (
        <div className="s-folha relatorios-escolha">
          <div className="s-folha-topo">
            <h2 className="s-folha-titulo">De qual caso?</h2>
          </div>
          {casos.length === 0 ? (
            <p className="vazio">
              Nenhum caso cadastrado ainda. O relatório nasce de um caso — abra um
              na aba Clientes.
            </p>
          ) : disponiveis.length === 0 ? (
            <p className="vazio">
              Todos os casos já têm relatório — há um por caso. Abra o que existe na
              lista abaixo, ou crie um caso novo.
            </p>
          ) : (
            <div className="lista-marcadores">
              {disponiveis.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="linha-escolha"
                  onClick={() => void abrirDoCaso(c.id)}
                >
                  <span className={`ponto-nivel nivel-${c.nivel}`} aria-hidden="true" />{' '}
                  <span className="dado">{c.codigo}</span>
                  {c.titulo ? ` · ${c.titulo}` : ''} — {c.nivelNome}
                  <span className="fantasia"> · {c.cliente.nome}</span>
                  <span className="relatorios-seta" aria-hidden="true">
                    <Icone nome="avancar" tamanho={16} />
                  </span>
                </button>
              ))}
            </div>
          )}
          <div className="s-acoes">
            <span className="dir">
              <button type="button" className="botao-secundario s-botao s-botao-p" onClick={() => setEscolhendo(false)}>
                Cancelar
              </button>
            </span>
          </div>
        </div>
      )}

      {relatorios === null ? (
        <p className="vazio">Carregando…</p>
      ) : relatorios.length === 0 ? (
        <p className="vazio">
          Nenhum relatório ainda.{' '}
          <strong>A estrutura do documento está sendo definida do zero</strong> —
          seção por seção, com o propósito e a automação de cada uma. Relatório
          criado agora nasce sem seção, e serve para conferir a estrutura conforme
          ela cresce.
        </p>
      ) : (
        <section className="s-folha relatorios-lista" aria-label="Lista de relatórios">
          {/*
            ⭐ **A faixa da busca, do filtro e da ordem — PRD §73, 29/10/2026.**
            ⚠️ Discreta de propósito: *"Ao invés de 'botões/tag' grandes,
            poderia ser mais sútil, com icones"*. O filtro é o grupo colado do
            "Mês | Lista" do Marketing; a ordem, o seletor em pílula da ficha.
          */}
          <div className="s-folha-topo relatorios-lista-topo">
            <div className="relatorios-busca">
              <label htmlFor="busca-relatorios" className="so-leitor-de-tela">
                Buscar relatório
              </label>
              <Icone nome="lupa" tamanho={16} />
              <input
                id="busca-relatorios"
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Número, devedor, cliente ou caso"
                autoComplete="off"
              />
            </div>
            {/* ⛔ O número de cada botão é o que o clique mostra (30/09/2026):
                com busca digitada, conta só o que ela achou. */}
            <div className="relatorios-filtro" role="tablist" aria-label="Situação do relatório">
              {FILTROS_DA_LISTA.map((f) => (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={filtro === f}
                  title={FILTROS[f].nome}
                  className={filtro === f ? 'on' : undefined}
                  onClick={() => filtrar(f)}
                >
                  <Icone nome={FILTROS[f].icone} tamanho={15} />
                  <span className="relatorios-filtro-nome">{FILTROS[f].nome}</span>
                  <b className="dado">{contagem[f]}</b>
                </button>
              ))}
            </div>
            <select
              className="s-seletor relatorios-ordem"
              value={ordem}
              onChange={(e) => ordenar(e.target.value as OrdemDaLista)}
              aria-label="Ordem da lista"
            >
              {ORDENS_DA_LISTA.map((o) => (
                <option key={o} value={o}>
                  {ORDENS[o]}
                </option>
              ))}
            </select>
          </div>

          <div className="relatorios-linhas">
          {visiveis.length === 0 && (
            <p className="vazio relatorios-nada">
              Nenhum relatório
              {filtro === 'andamento' ? ' em andamento' : filtro === 'entregues' ? ' entregue' : ''}
              {busca.trim() ? <> com “{busca.trim()}”. </> : '. '}
              {busca.trim() ? (
                <button type="button" className="link-interno" onClick={() => setBusca('')}>
                  Limpar a busca
                </button>
              ) : (
                <button type="button" className="link-interno" onClick={() => filtrar('todos')}>
                  Ver todos
                </button>
              )}
            </p>
          )}
          {visiveis.map((r) => (
            <article key={r.id} className={`contrato relatorio-linha nivel-${r.caso.nivel}`}>
              <span className="s-avatar relatorio-linha-icone" aria-hidden="true">
                <Icone nome="relatorios" tamanho={17} />
              </span>
              <div className="relatorio-linha-corpo">
              <div className="contrato-topo">
                <div className="contrato-identidade">
                  {/* O código abre a ficha. Alvo de clique é o nome do que se
                      quer abrir — não um botão ao lado dele. */}
                  <button
                    type="button"
                    className="dado contrato-codigo link-interno"
                    onClick={() => aoAbrir(r.id)}
                  >
                    {r.codigo}
                  </button>
                  <span className={`selo-nivel selo-${r.caso.nivel}`}>{r.caso.nivelNome}</span>
                  {/* ⭐ A porcentagem escrita, ou a data da entrega, no lugar de
                      "Rascunho" — 14/09/2026 (`lib/avanco-do-relatorio.ts`). */}
                  <span className={avancoDoRelatorio(r).classe}>{avancoDoRelatorio(r).texto}</span>
                  <PilulaDoFundo consumido={r.caso.fundo?.consumido} />
                </div>
              </div>

              {/* A linha do caso leva ao caso: é a outra metade do vínculo que
                  o Richard pediu em 16/08/2026. */}
              <p className="ajuda">
                <button
                  type="button"
                  className="link-interno"
                  onClick={() => aoAbrirCliente(r.cliente.id)}
                >
                  {r.cliente.nome} · caso <span className="dado">{r.caso.codigo}</span>
                  {r.caso.titulo ? ` · ${r.caso.titulo}` : ''}
                </button>
              </p>

              {/*
                ⛔ **A BARRA E A CONTAGEM SAÍRAM — 31/08/2026, decisão dele.**

                ⭐ **E ela tinha acabado de deixar de dizer a verdade:** no mesmo
                dia o campo fixo de cada seção passou a só aparecer quando tem
                texto, e o trabalho migrou para os campos livres. A barra media
                apenas o campo fixo — mostraria zero por cento num relatório
                inteiro respondido.

                ⚠️ **A data de entrega FICOU**, porque essa a lista continua
                respondendo. ⚠️ E a classe `.progresso` **não foi apagada do
                CSS**: ela é a mesma barra de recebimento do contrato, na ficha
                do cliente, que é desenho de dinheiro noutra aba.
              */}

              {!ehMaster && r.situacao !== 'entregue' && (
                <p className="ajuda">Entregar é do master.</p>
              )}
              </div>
              <button
                type="button"
                className="botao-secundario s-botao s-botao-p relatorio-linha-abrir"
                onClick={() => aoAbrir(r.id)}
              >
                {r.situacao === 'entregue' ? 'Ver' : 'Escrever'}
                <Icone nome="avancar" tamanho={15} />
              </button>
            </article>
          ))}
          </div>
        </section>
      )}
    </section>
  );
}
